"use client";

import { createContext, useContext } from "react";

/**
 * Host-derived chat UI facts, computed server-side in AuthenticatedContent:
 *
 * - enabled: whether chat affordances (sidebar link, new chat, ⌘K entries,
 *   history) render on this request — the org's chat opt-in (AIR-813) AND
 *   the dedicated-host split must both allow it.
 * - chatPath: where the chat surface lives on this host — "/" on the
 *   dedicated chat host (AIR-828), "/chat" everywhere else (the client
 *   mirror of chatLandingPath in lib/chat/chat-host.ts). Client navigations
 *   must target it directly rather than "/chat"; the middleware /chat*
 *   canonicalization stays as the safety net for external URLs only.
 * - homeHref: where "go home" affordances point — the portal home as an
 *   ABSOLUTE URL while the host split is active (AIR-1169: on the chat host
 *   a relative /home only resolves via the middleware's 308 portal bounce,
 *   and session cookies are host-only), the plain relative path in
 *   single-host environments.
 *
 * The defaults cover the shells that render this tree without
 * AuthenticatedContent (logged-out /chat) — enforcement lives in the chat
 * page and POST /api/chat, so an unwrapped tree only affects chrome.
 */
const ChatUiContext = createContext<{
  enabled: boolean;
  chatPath: string;
  homeHref: string;
}>({
  enabled: true,
  chatPath: "/chat",
  homeHref: "/home",
});

export function ChatUiProvider({
  enabled,
  chatPath,
  homeHref,
  children,
}: {
  enabled: boolean;
  chatPath: string;
  homeHref: string;
  children: React.ReactNode;
}) {
  return (
    <ChatUiContext value={{ enabled, chatPath, homeHref }}>{children}</ChatUiContext>
  );
}

export function useChatUiEnabled(): boolean {
  return useContext(ChatUiContext).enabled;
}

/** The chat surface's path on this host ("/" on the chat host, else "/chat"). */
export function useChatPath(): string {
  return useContext(ChatUiContext).chatPath;
}

/** The portal home href: absolute while the host split is active, else "/home". */
export function useHomeHref(): string {
  return useContext(ChatUiContext).homeHref;
}

/** The URL for a conversation (or the empty chat when no id): host-aware. */
export function chatUrl(chatPath: string, chatId?: string): string {
  return chatId ? `${chatPath}?chat=${chatId}` : chatPath;
}
